import { notFound } from 'next/navigation';
import { z } from 'zod';
import { getRevalidateTime } from '../../../utils/cache';
import { getEnvVar } from '../../../utils/env';

const PUBLISHER_ID = getEnvVar('NEXT_PUBLIC_PUBLISHER_ID');
const DEFAULT_ASSET_SORT_BY = getEnvVar('NEXT_PUBLIC_DEFAULT_ASSET_SORT_BY');

// TODO: ensure selected language is always put into header 'Accept-Language' when fetching assets

const ImageTemplates = z.object({
  regions: z.array(z.string()),
  locations: z.array(z.string()),
  withRegion: z.string(),
  withLocation: z.string(),
  complete: z.string(),
});

const Images = z.object({
  defaultUrl: z.string(),
  version: z.string(),
  templates: ImageTemplates,
});

const Publishing = z.object({
  start: z.string().optional(),
  platform: z.string(),
  geoExpressions: z.array(z.string()),
});

const Product = z.object({
  id: z.string(),
  name: z.string(),
  accessType: z.string(),
});

const AccessInfo = z.object({
  accessType: z.string(),
});

const CategoryLink = z.object({
  path: z.string().optional(),
  link: z.string(),
  id: z.string(),
});

const Version = z.object({
  available: z.array(z.string()),
  type: z.string(),
});

const Marker = z.object({
  id: z.string(),
  title: z.string().optional(),
  type: z.string(),
  startTime: z.number().optional(),
  endTime: z.number().optional(),
});

export const Asset = z.object({
  id: z.string(),
  timestamp: z.string(),
  createTime: z.string(),
  images: Images.optional(),
  content: z
    .object({
      images: Images.optional(),
    })
    .optional(),
  category: CategoryLink,
  labeledAsFree: z.boolean(),
  drmProtected: z.boolean(),
  duration: z.number(),
  assetType: z.string(),
  aspect16x9: z.boolean().optional(),
  isLive: z.boolean(),
  channelNumber: z.number().optional(),
  isParent: z.boolean(),
  transmissionTime: z.string(),
  relatedAssets: z.array(z.unknown()),
  qualities: z.array(z.unknown()).optional(),
  startTime: z.number().optional(),
  endTime: z.number().optional(),
  version: Version,
  publishing: Publishing,
  products: z.array(Product),
  accessInfo: AccessInfo.optional(),
  'publisher-id': z.number().optional(),
  'description-short': z.string().optional(),
  description: z.string().optional(),
  title: z.string(),
  labels: z.string().optional(),
  people: z.string().optional(),
  tags: z.string().optional(),
  keywords: z.string().optional(),
  'age-rating': z.string().optional(),
  markers: z.array(Marker).optional(),
  season: z.number().optional(),
  episode: z.number().optional(),
  adProfile: z.string().optional(),
  'reference-links': z.string().optional(),
});

const Meta = z.object({
  numberOfHits: z.number(),
  totalPages: z.number().optional(),
  pageSize: z.number().optional(),
});

const Links = z.object({
  self: z.string(),
  first: z.string(),
  last: z.string(),
});

const ErrorInfo = z.object({
  title: z.string(),
  detail: z.string(),
  code: z.string(),
});

export type AssetType = z.infer<typeof Asset>;

const assetsResponseValidator = z.object({
  data: z.array(Asset).optional(),
  meta: Meta.optional(),
  links: Links.optional(),
  errors: z.array(ErrorInfo).optional(),
});

export type Page = {
  number?: number;
  size: number;
};

export const getAsset = async ({
  assetId,
  locale,
  markers = false,
}: {
  assetId: string;
  locale?: string | undefined;
  markers?: boolean;
}) => {
  const headers = {
    accept: 'application/json',
    ...(locale && { 'Accept-Language': locale }),
  } as HeadersInit;
  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };
  let queryParams = '';
  if (markers) {
    queryParams = '?extraFields=markers';
  }
  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/assets/${assetId}${queryParams}`,
    requestOptions,
  )
    .then((res) => {
      return res.json();
    })
    .then((json) => {
      const response = assetsResponseValidator.parse(json);
      return response.data ? response.data[0] : undefined;
    })
    .catch((e) => {
      console.error('Error fetching asset', e);
    });
};

type NextFetchRequestInit = RequestInit & {
  next?: {
    revalidate?: number;
    tags?: string[];
  };
};

export const getAssets = async ({
  assetIds,
  locale,
}: {
  assetIds: string[];
  locale?: string;
}) => {
  const headers: HeadersInit = {
    accept: 'application/json',
    ...(locale && { 'Accept-Language': locale }),
  };
  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };
  if (assetIds?.length === 0) {
    return Promise.resolve(undefined);
  }

  const query =
    `id:(${assetIds.join(' ')})` +
    (PUBLISHER_ID ? ` AND publisher-id:${PUBLISHER_ID}` : '');

  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar(
      'NEXT_PUBLIC_ENVIRONMENT',
    )}.vmnd.tv/api/v1/assets?query=${query}`,
    requestOptions,
  )
    .then((res) => res.json())
    .then((json) => {
      const response = assetsResponseValidator.parse(json);
      return response.data;
    });
};

export const getAssetsByCategory = async ({
  categoryId,
  includeAssetsFromChildCategories,
  extraQueryClause,
  locale,
  page,
}: {
  categoryId: string;
  includeAssetsFromChildCategories?: boolean;
  extraQueryClause?: string;
  locale?: string;
  page?: Page;
}) => {
  const categoryClause = includeAssetsFromChildCategories
    ? `category.path:${categoryId}`
    : `category.id:${categoryId}`;
  const query =
    categoryClause +
    (extraQueryClause ?? '') +
    (PUBLISHER_ID ? ` AND publisher-id:${PUBLISHER_ID}` : '');

  const headers: HeadersInit = {
    accept: 'application/json',
    ...(locale && { 'Accept-Language': locale }),
  };
  const requestOptions: NextFetchRequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
    next: {
      revalidate: getRevalidateTime(),
    },
  };
  const queryParams =
    `query=${query}` +
    '&extraFields=markers' +
    (DEFAULT_ASSET_SORT_BY ? `&sort=${DEFAULT_ASSET_SORT_BY}` : '');
  let url = `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/assets?${queryParams}`;
  if (page?.number) {
    url += `&page[number]=${page.number}`;
  }
  if (page?.size) {
    url += `&page[size]=${page.size}`;
  }
  return fetch(url, requestOptions)
    .then((res) => res.json())
    .then((json) => {
      const response = assetsResponseValidator.parse(json);
      return response.data!;
    });
};

export type PaginatedAssetsResult = {
  assets: AssetType[];
  hasMore: boolean;
  totalPages?: number;
  pageSize?: number;
};

export const getAssetsByCategoryPaginated = async ({
  categoryId,
  includeAssetsFromChildCategories,
  extraQueryClause,
  locale,
  page,
}: {
  categoryId: string;
  includeAssetsFromChildCategories?: boolean;
  extraQueryClause?: string;
  locale?: string;
  page: Page;
}): Promise<PaginatedAssetsResult> => {
  const categoryClause = includeAssetsFromChildCategories
    ? `category.path:${categoryId}`
    : `category.id:${categoryId}`;
  const query =
    categoryClause +
    (extraQueryClause ?? '') +
    (PUBLISHER_ID ? ` AND publisher-id:${PUBLISHER_ID}` : '');

  const headers: HeadersInit = {
    accept: 'application/json',
    ...(locale && { 'Accept-Language': locale }),
  };
  const requestOptions: NextFetchRequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
    next: {
      revalidate: getRevalidateTime(),
    },
  };
  const queryParams =
    `query=${query}` +
    '&extraFields=markers' +
    (DEFAULT_ASSET_SORT_BY ? `&sort=${DEFAULT_ASSET_SORT_BY}` : '');
  let url = `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/assets?${queryParams}`;
  if (page.number) {
    url += `&page[number]=${page.number}`;
  }
  if (page.size) {
    url += `&page[size]=${page.size}`;
  }
  return fetch(url, requestOptions)
    .then((res) => res.json())
    .then((json) => {
      const response = assetsResponseValidator.parse(json);
      const assets = response.data ?? [];
      const totalPages = response.meta?.totalPages;
      const pageSize = response.meta?.pageSize ?? page.size;
      const currentPage = page.number ?? 1;

      // Determine if there are more pages
      const hasMore = totalPages
        ? currentPage < totalPages
        : assets.length >= page.size;

      return {
        assets,
        hasMore,
        totalPages,
        pageSize,
      };
    })
    .catch((e) => {
      console.error('Error fetching paginated assets by category', e);
      return {
        assets: [],
        hasMore: false,
        totalPages: 0,
        pageSize: page.size,
      };
    });
};

export const searchAssets = async (
  query: string,
  locale?: string,
  encode = true,
) => {
  const headers: HeadersInit = {
    accept: 'application/json',
    ...(locale && { 'Accept-Language': locale }),
  };
  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };

  const publisherId = getEnvVar('NEXT_PUBLIC_PUBLISHER_ID');
  const fullQuery =
    `(${query})` + (publisherId ? ` AND publisher-id:${publisherId}` : '');

  const searchQuery = encode ? encodeURIComponent(fullQuery) : fullQuery;

  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/assets?query=${searchQuery}&page[size]=50`,
    requestOptions,
  )
    .then((res) => {
      return res.json();
    })
    .then((json) => {
      const response = assetsResponseValidator.parse(json);
      if (response.errors) {
        return notFound();
      }
      return response.data ?? undefined;
    });
};

export const searchLatestAssets = async (locale?: string) => {
  const headers: HeadersInit = {
    accept: 'application/json',
    ...(locale && { 'Accept-Language': locale }),
  };
  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };

  const query =
    'query=isParent:true' +
    (PUBLISHER_ID ? ` AND publisher-id:${PUBLISHER_ID}` : '');

  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/assets?query=${query}&page[size]=24&sort=-transmissionTime`,
    requestOptions,
  )
    .then((res) => {
      return res.json();
    })
    .then((json) => {
      const response = assetsResponseValidator.parse(json);
      if (response.errors) {
        return notFound();
      }
      return response.data ?? undefined;
    });
};
